import { useEffect, useState } from 'react';
import { Avatar, Button, Card, Col, Input, message, Row, Select, Tag, Typography } from 'antd';
import { UserOutlined, CheckCircleFilled, LinkOutlined, PlusOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { fetchCompanies, fetchConnections, connectToCompany, disconnectFromCompany, Company, Connection } from '../api/network';

const { Title, Text } = Typography;

const TYPES = ['All', 'farmer', 'artisan', 'restaurant', 'service_provider', 'cooperative', 'ngo', 'other'];

export default function Directory() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  useEffect(() => {
    if (!token) return;
    Promise.all([fetchCompanies(token), fetchConnections(token)])
      .then(([c, conn]) => { setCompanies(c); setConnections(conn); })
      .finally(() => setLoading(false));
  }, [token]);

  async function toggle(company: Company) {
    if (!token) return;
    const existing = connections.find(c => c.company_id === company.id);
    setToggling(company.id);
    if (existing) {
      const ok = await disconnectFromCompany(token, company.id);
      if (ok) {
        setConnections(prev => prev.filter(c => c.company_id !== company.id));
        message.success(`Disconnected from ${company.name}`);
      } else {
        message.error('Failed to disconnect');
      }
    } else {
      const conn = await connectToCompany(token, company.id);
      if (conn) {
        setConnections(prev => [...prev, conn]);
        message.success(`Connected to ${company.name}`);
      } else {
        message.error('Failed to connect');
      }
    }
    setToggling(null);
  }

  const connectedIds = new Set(connections.map(c => c.company_id));

  const filtered = companies.filter(c => {
    const matchSearch = search === '' || c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.city ?? '').toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'All' || c.company_type === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Business Directory</Title>
        <Text type="secondary">{filtered.length} businesses</Text>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Input.Search
          placeholder="Search businesses..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
          allowClear
        />
        <Select value={typeFilter} onChange={setTypeFilter} style={{ width: 180 }}>
          {TYPES.map(t => <Select.Option key={t} value={t}>{t === 'All' ? 'All Types' : t.replace('_', ' ')}</Select.Option>)}
        </Select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>Loading businesses...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>No businesses found.</div>
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map(company => {
            const isConnected = connectedIds.has(company.id);
            const isToggling = toggling === company.id;
            return (
              <Col key={company.id} xs={24} sm={12} lg={8}>
                <Card
                  hoverable
                  actions={[
                    <Button
                      icon={isToggling ? <LoadingOutlined /> : isConnected ? <CheckOutlined /> : <PlusOutlined />}
                      type="text"
                      style={{ color: isConnected ? '#52c41a' : undefined }}
                      loading={isToggling}
                      onClick={() => toggle(company)}
                    >
                      {isConnected ? 'Connected' : 'Connect'}
                    </Button>,
                    company.website
                      ? <Button icon={<LinkOutlined />} type="text" href={company.website} target="_blank">Website</Button>
                      : <Button icon={<LinkOutlined />} type="text" disabled>No site</Button>,
                  ]}
                >
                  <Card.Meta
                    avatar={
                      company.logo_url
                        ? <Avatar size={48} src={company.logo_url} />
                        : <Avatar size={48} icon={<UserOutlined />} style={{ background: '#722ed1' }} />
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {company.name}
                        {company.is_verified && <CheckCircleFilled style={{ color: '#1677ff', fontSize: 14 }} />}
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <Tag color="blue">{company.company_type.replace('_', ' ')}</Tag>
                          {company.city && <Text type="secondary" style={{ fontSize: 12 }}>{company.city}{company.country ? `, ${company.country}` : ''}</Text>}
                        </div>
                        {company.description && (
                          <Text type="secondary" style={{ fontSize: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {company.description}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}
